import { GridApi, GridReadyEvent, ITooltipParams } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { FC, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import Button from '../Button/Button';
import { ButtonProps } from '../Button/Button.types';
import InputButton from '../InputButton/InputButton';
import { InputButtonProps } from '../InputButton/InputButton.types';
import NoRowsOverlay from './NoRowsOverlay/NoRowsOverlay';
import {
  Container,
  FooterButtons,
  GridThemeWrapper,
  HeaderInputButton,
  StyledFooter,
  StyledFooterText,
  StyledHeader,
  StyledHeaderTitle,
  StyledReadOnlyLabel,
} from './Table.styles';
import { TableProps } from './Table.types';

export const tableToolTipValueGetter = (params: ITooltipParams) => params.value;

const Table: FC<TableProps> = forwardRef(
  (
    {
      height = '500px',
      width = '100%',
      showHeader = false,
      showFooter = true,
      headerTitle,
      headerButtons,
      footerButtons,
      returnedRecords,
      readOnlyLabel,
      onGridReady,
      rowData,
      defaultColDef,
      noRowsMessage,
      isSizeColumnsToFit = true,
      ...props
    },
    ref: ForwardedRef<AgGridReact>
  ) => {
    const [gridApi, setGridApi] = useState<GridApi>();
    const [displayedRowCount, setDisplayedRowCount] = useState<number | undefined>(returnedRecords);

    // Function to get nested property value from an object using dot notation
    const getNestedValue = (obj: any, path: string): any => {
      return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    // Function to determine if a column contains only numeric values
    const isNumericColumn = (field: string): boolean => {
      if (!rowData || rowData.length === 0) return false;
      
      // Check a sample of rows to determine if column is numeric
      const sampleSize = Math.min(rowData.length, 10);
      let validValueCount = 0;
      
      for (let i = 0; i < sampleSize; i++) {
        const value = getNestedValue(rowData[i], field);
        
        // Skip null, undefined, and empty string values
        if (value !== null && value !== undefined && value !== '') {
          validValueCount++;
          const numValue = Number(value);
          // Check if value is numeric and exclude decimal numbers
          if (isNaN(numValue) || !Number.isInteger(numValue)) {
            return false;
          }
        }
      }
      
      // If we have no valid values to check, default to text filtering
      return validValueCount > 0;
    };

    // Disables the floating filters when there are no records
    const [internalDefaultColDef, setInternalDefaultColDef] = useState({
      resizable: true,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        readOnly: returnedRecords === 0 ? true : false,
        defaultOption: 'equals',
        suppressAndOrCondition: true,
      },
    });
    // updates the filter state when returnedRecords changes
    useEffect(() => {
      setInternalDefaultColDef({
        ...internalDefaultColDef,
        filterParams: {
          readOnly: returnedRecords === 0 ? true : false,
          defaultOption: 'contains', // Default to contains, will be overridden by column-specific logic
          suppressAndOrCondition: true,
        },
      });
      gridApi?.refreshHeader();
      setDisplayedRowCount(returnedRecords);
    }, [returnedRecords]);

    // Function to enhance column definitions with appropriate filter settings
    const enhanceColumnDefs = (columnDefs: any[]) => {
      if (!rowData || rowData.length === 0) return columnDefs;
      
      return columnDefs.map((colDef: any) => {
        if (colDef.field) {
          const isNumeric = isNumericColumn(colDef.field);
          return {
            ...colDef,
            filterParams: {
              ...colDef.filterParams,
              readOnly: returnedRecords === 0 ? true : false,
              defaultOption: isNumeric ? 'equals' : 'contains',
              suppressAndOrCondition: true,
            },
          };
        }
        return colDef;
      });
    };

    useEffect(() => {
      if (gridApi && process.env.NODE_ENV !== 'test' && isSizeColumnsToFit) {
        gridApi.sizeColumnsToFit();
      }
    }, [gridApi]);    const displayHeaderButtons = headerButtons?.map((buttonProps: ButtonProps | InputButtonProps) => {
      if ((buttonProps as InputButtonProps)?.buttonProps) {
        const props = buttonProps as InputButtonProps;
        return (
          <HeaderInputButton key={props.buttonProps.label}>
            <InputButton {...props} />
          </HeaderInputButton>
        );
      } else {
        const props = buttonProps as ButtonProps;
        return <Button key={props.label} {...props} />;
      }
    });

    const displayFooterButtons = footerButtons?.map((buttonProps: ButtonProps) => {
      return <Button key={buttonProps.label} {...buttonProps} />;
    });

    const handleGridReady = (event: GridReadyEvent) => {
      if (onGridReady) {
        onGridReady(event);
      }
      setGridApi(event.api);
    };

    // shows NoRowsOverlay when there are no rows to display after filter
    const handleModelUpdate = () => {
      if (displayedRowCount === 0) {
        gridApi?.showNoRowsOverlay();
      } else {
        gridApi?.hideOverlay();
      }
    };

    const handleFilterChange = () => {
      setDisplayedRowCount(gridApi?.getDisplayedRowCount() || 0);
    };

    return (
      <Container className='ag-theme-alpine' height={height} width={width}>
        {showHeader ? (
          <StyledHeader data-testid='header'>
            <StyledHeaderTitle>{headerTitle}</StyledHeaderTitle>
            {displayHeaderButtons}
          </StyledHeader>
        ) : (
          <></>
        )}
        <GridThemeWrapper
          height={height}
          showHeader={showHeader}
          showFooter={showFooter}
          headerInputButton={
            headerButtons &&
            headerButtons?.length > 0 &&
            (headerButtons[0] as InputButtonProps)?.buttonProps !== undefined
          }
        >
          <AgGridReact
            {...props}
            suppressColumnVirtualisation={props.suppressColumnVirtualisation || process.env.NODE_ENV === 'test'}
            suppressRowVirtualisation={props.suppressRowVirtualisation || process.env.NODE_ENV === 'test'}
            ref={ref}
            onGridReady={handleGridReady}
            rowData={rowData}
            columnDefs={props.columnDefs ? enhanceColumnDefs(props.columnDefs) : props.columnDefs}
            defaultColDef={defaultColDef ? defaultColDef : internalDefaultColDef}
            noRowsOverlayComponent={NoRowsOverlay}
            noRowsOverlayComponentParams={{ message: noRowsMessage }}
            singleClickEdit={props.singleClickEdit || true}
            stopEditingWhenCellsLoseFocus={props.stopEditingWhenCellsLoseFocus || true}
            onFilterChanged={handleFilterChange}
            onModelUpdated={handleModelUpdate}
          />
        </GridThemeWrapper>
        {/* show footer buttons on a separate row than returnedRecords if button count >= 3 */}
        {showFooter && footerButtons && footerButtons.length >= 3 ? (
          <FooterButtons>{displayFooterButtons}</FooterButtons>
        ) : (
          <></>
        )}
        {showFooter && (
          <StyledFooter footerButtons={footerButtons}>
            {footerButtons && footerButtons.length < 3 && displayFooterButtons}
            <StyledFooterText>
              {`${displayedRowCount === undefined ? 0 : displayedRowCount} ${
                displayedRowCount === 1 ? 'record' : 'records'
              }`}
            </StyledFooterText>
          </StyledFooter>
        )}
        {readOnlyLabel && <StyledReadOnlyLabel>{readOnlyLabel}</StyledReadOnlyLabel>}
      </Container>
    );
  }
);

export default Table;
